import { HubConnectionState } from '@microsoft/signalr';
import { useEffect, useMemo } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useFileExportContext } from 'src/contexts/FileExportContext';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { downloadFileFromBlob } from 'src/utils/fileUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { Feature, FileResponse, ReportFileType } from 'src/WebApiController';

import { ReportExportOptionsV2 } from './ReportsExport.types';
import { getReportExportFileBasename } from './ReportsExport.utils';

const useReportExportBackgroundProcess = (snapshotDate: Date | undefined) => {
  const { fileExportHub } = useFileExportContext();
  const isExportSnapshot = snapshotDate != null;
  const hasReportSnapshotBackgroundExportFeature = useUserHasFeature(
    Feature.ReportSnapshotBackgroundExport
  );
  const hasReportBackgroundExportFeature = useUserHasFeature(
    Feature.ReportBackgroundExport
  );
  const hasFileExportHubActive =
    fileExportHub?.state === HubConnectionState.Connected;

  const supportBackgroundProcess = useMemo(
    () =>
      hasFileExportHubActive &&
      ((isExportSnapshot && hasReportSnapshotBackgroundExportFeature) ||
        (!isExportSnapshot && hasReportBackgroundExportFeature)),
    [
      hasFileExportHubActive,
      hasReportBackgroundExportFeature,
      hasReportSnapshotBackgroundExportFeature,
      isExportSnapshot,
    ]
  );

  return {
    supportBackgroundProcess,
  };
};

export function useReportsExportV2Inner<T extends ReportConfigV2['request']>({
  reportExportOptions: { reportToExport: reportConfig, fileType, snapshotDate },
  onReportExported,
  downloadReportMetrics,
}: {
  reportExportOptions: ReportExportOptionsV2;
  onReportExported: () => void;
  downloadReportMetrics: (
    req: T,
    reportName: string | undefined,
    fileType: ReportFileType | undefined,
    supportBackgroundProcess: boolean
  ) => Promise<FileResponse>;
}) {
  const { trackError } = useClientLogContext();
  const { setFileExportIsPending } = useFileExportContext();
  const { supportBackgroundProcess } =
    useReportExportBackgroundProcess(snapshotDate);

  const downloadReport = async () => {
    const req = {
      ...reportConfig.request,
    } as T;

    if (snapshotDate) {
      req.snapshotTime = snapshotDate.toISOString();
    }

    await tryInvokeApi(
      async () => {
        if (supportBackgroundProcess) {
          setFileExportIsPending(true);
        }
        const result = await downloadReportMetrics(
          req,
          reportConfig.reportName,
          fileType,
          supportBackgroundProcess
        );

        if (result.data && !supportBackgroundProcess) {
          downloadFileFromBlob(
            `${getReportExportFileBasename(
              reportConfig.reportName,
              snapshotDate
            )}.${fileType.toLowerCase()}`,
            result.data
          );
        }
      },
      (error) => {
        trackError('ReportV2Client.downloadReportMetrics', error);
      },
      () => onReportExported()
    );
  };

  useEffect(() => {
    downloadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportConfig, fileType, snapshotDate]);
}
